import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { ILogin } from '@app/types';
import { authApi } from '@app/store/services/auth';
import styled from '@2pulse/responsive';
import { KeyboardAvoidingView, TouchableOpacity } from 'react-native';
import { TextInput } from '@app/components';
import { Button, Form } from '@app/components';
import { StyledForms } from '@app/components/styled/form/Form';
import { useToast } from '@app/hooks';
import { extractApiGenericError } from '@app/helpers';
import { useAppDispatch } from '@app/store/hooks';
import { appSlice } from '@app/store';
import { useNavigation } from '@react-navigation/native';

const LoginView = styled.View({
  width: '100%',
  height: '100%',
  flex: 1,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: '#02182B',
});

const LoginForm = styled.View({
  width: 420,
});

const LoginSubmit = styled.View({
  width: 300,
  marginLeft: 60,
});

const Logo = styled.Image({
  marginTop: 21,
  marginBottom: 34,
  alignSelf: 'center',
  height: 70,
  width: 345,
});

const TextError = styled.Text({
  color: 'red',
  marginBottom: 10,
});

const ForgotPasswordLink = styled.Text({
  color: 'white',
  fontSize: 14,
  textAlign: 'center',
  marginTop: 20,
  textDecorationLine: 'underline',
});

const schema = yup
  .object({
    email: yup.string().required('Ce champ est requis'),
    password: yup.string().required('Ce champ est requis'),
  })
  .required();

export const Login = () => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const navigation = useNavigation();

  const formMethods = useForm<ILogin>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { errors } = formMethods.formState;

  const [login, { isLoading }] = authApi.useLoginMutation();

  const onSubmit = async (data: ILogin) => {
    try {
      await login(data).unwrap();

      dispatch(appSlice.actions.setSelectedShop(undefined));
    } catch (error: any) {
      if (error.status === 401) {
        toast.error('Email ou mot de passe incorrect');
        return;
      }

      const errorMessage = extractApiGenericError(error) ?? 'Une erreur est survenue lors de la connexion.';
      toast.error(errorMessage);
    }
  }

  return (
    <LoginView>
      <KeyboardAvoidingView behavior="position">
        <Logo source={require('@app/assets/img/logo.png')} />

        {errors.email && <TextError>{errors.email.message}</TextError>}

        <LoginForm>
          <Form formMethods={formMethods}>

            <StyledForms.Form>

              <TextInput
                name="email"
                label="Email"
                placeholderTextColor="black"
                autoCapitalize="none"
                autoComplete="off"
                onSubmitEditing={formMethods.handleSubmit(onSubmit)}
              />

              {errors.password && <TextError>{errors.password.message}</TextError>}

              <TextInput
                name="password"
                label="Mot de passe"
                placeholderTextColor="black"
                secureTextEntry={true}
                autoCapitalize="none"
                autoComplete="off"
                onSubmitEditing={formMethods.handleSubmit(onSubmit)}
              />

              <LoginSubmit>
                <Button
                  large
                  title='Connexion'
                  loading={isLoading}
                  onPress={formMethods.handleSubmit(onSubmit)} />
              </LoginSubmit>

            </StyledForms.Form>

          </Form>

        </LoginForm>

        <TouchableOpacity onPress={() => navigation.navigate('ForgotPassword' as never)}>
          <ForgotPasswordLink>Mot de passe oubli&eacute; ?</ForgotPasswordLink>
        </TouchableOpacity>
      </KeyboardAvoidingView>

    </LoginView>
  );
};
